import { useLazyQuery } from "@apollo/client";
import { clientNeoAuth } from "@neonumy/api-clients";
import { FormInstance } from "antd5";
import { routers } from "config/routes";
import useStore from "config/store";
import dayjs from "dayjs";
import useGeneralPermissions from "hooks/Permissions/useGeneralPermissions";
import { GET_USER_DATA_QUERY } from "queries/neoauth/users";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { createOptionCountry } from "utils/addressOptions";
import minArrayObjectElement from "utils/minArrayObjectElement";
import { onErrorGraphql } from "utils/onErrorGraphql";
import { USER_ROUTES } from "views/Collaborators/utils/userRoutes";
import useCurrentUserGroups from "./useCurrentUserGroups";

interface GetUserQueryProps {
  form: FormInstance;
  groups: UserGroupType[];
  setApplicationGroups: Dispatch<SetStateAction<UserGroupType[]>>;
}

export default function useGetUserQuery({ form, groups, setApplicationGroups }: GetUserQueryProps) {
  const userData = useStore(state => state.userData);
  const { canUpdateUser: canUpdateUserPermission } = useGeneralPermissions();
  const { groupDefaultOrder } = useCurrentUserGroups();

  const [userIsClient, setUserIsClient] = useState(false);
  const [userCategory, setUserCategory] = useState<number>();
  const [userGroups, setUserGroups] = useState<ID[]>();
  const [canUpdateUser, setCanUpdateUser] = useState(true);
  const [hideApplicationGroupsInput, setHideApplicationGroupsInput] = useState(false);

  const [getUser, { loading: getUserLoading, error }] = useLazyQuery<
    GetUserQueryOutput,
    GetUserQueryInput
  >(GET_USER_DATA_QUERY, {
    client: clientNeoAuth,
  });

  const fetchUser = async (id: number) => {
    const { data } = await getUser({ variables: { id } });
    if (!data) return;

    const user = data.getUser;
    if (!user) {
      routers.navigate(USER_ROUTES.users, { replace: true });
      return;
    }

    const userDefaultGroup = getUserDefaultGroup(user.groups);

    setCanUpdateUser(
      (canUpdateUserPermission &&
        groupDefaultOrder !== undefined &&
        userDefaultGroup.order > groupDefaultOrder) ||
        userData.isSuperuser,
    );

    const isNewraiUser = user.isNewraiUser;
    const isClient = user.userCategory.slug === "client";
    setUserIsClient(isClient);
    setUserCategory(Number(user?.userCategory.id));

    const userType = isNewraiUser ? "newrai" : isClient ? "client" : "standard";
    const groupsOptions = groups.filter(group => group.userType === userType);
    setApplicationGroups(groupsOptions);

    const { userGroups, userGroupValue, hideApplicationGroups } = resolveUserGroups(
      user,
      groupsOptions,
    );

    setUserGroups(userGroups);
    setHideApplicationGroupsInput(hideApplicationGroups);

    form.setFieldsValue(mapUserToForm(user, userGroupValue));
  };

  const getUserDefaultGroup = (groups: UserGroupsType[] = []) =>
    minArrayObjectElement(groups, "order") ?? ({ order: 999 } as UserGroupsType);

  const resolveUserGroups = (user: User, groupsOptions: UserGroupType[]) => {
    const userGroup = groupsOptions.find(group =>
      user.groups.some(item => item.slug === group.slug),
    );

    const restUserGroups = user.groups.filter(
      userGroup => !groupsOptions.some(opt => opt.slug === userGroup.slug),
    );

    const hasOnlyNeoFraisGroup =
      (user.permissions?.includes("neofrais.is-employee") ||
        user.permissions?.includes("neofrais.is-validator")) &&
      (user.permissions?.includes("neoauth.is-client") ||
        user.permissions?.includes("neoauth.is-client-without-accounting"));

    const userGroupValue = hasOnlyNeoFraisGroup ? user.groups[0]?.id : userGroup?.value;

    return {
      userGroups: restUserGroups.map(group => group.id),
      userGroupValue,
      hideApplicationGroups: hasOnlyNeoFraisGroup,
    };
  };

  const mapUserToForm = (user: User, userGroupValue?: ID) => ({
    ...user,
    applicationGroups: userGroupValue ?? null,
    startDate: user.startedWorkingAt && dayjs(user.startedWorkingAt),
    endDate: user.endedWorkingAt && dayjs(user.endedWorkingAt),
    workTime: user.workTime && dayjs(user.workTime, "HH:mm:ss"),
    country: user.countryData && createOptionCountry(user.countryData),
    workShift: user.workShift?.value,
  });

  useEffect(() => {
    if (error) onErrorGraphql(error);
  }, [error]);

  return {
    fetchUser,
    getUserLoading,
    userIsClient,
    canUpdateUser,
    userCategory,
    userGroups,
    hideApplicationGroupsInput,
  };
}
